import { Box, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Trash } from '@styled-icons/bootstrap/Trash';
import PartnerAutocompleteWrapper from 'FeatureModules/Activity/Components/Forms/PartnerAutocompleteWrapper';
import SitesAutocompleteWrapper from 'FeatureModules/Activity/Components/Forms/SiteMulitSelectionWrapper';
import documentVisibilityOptions, {
  TVisibilityStatusKeyValue,
} from 'FeatureModules/Document/utils/documentVisibility';
import DatePickerField from 'Components/Forms/DatePickerField';
import DropDown from 'Components/Forms/DropDown';
import TextInputField from 'Components/Forms/TextInputField';
import TextInputFileExtension from 'Components/Forms/TextInputFileExtension';
import { mergeDefaults } from 'utils/form.utils';
import useDialog from 'hooks/useDialog';
import { OTHER_CERTIFICATE_ID } from 'FeatureModules/Document/utils/document.utils';
import { documentSchema } from 'FeatureModules/Document/schemas/document.schema';
import { FieldWrapper, ThemeButton, ThemeTypography } from 'DesignSystem';
import { FastField as Field, Form, Formik } from 'formik';
import { TCreateDocumentInput } from 'graphql/secure/mutations/types/document-mutation.types';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import omitBy from 'lodash/omitBy';
import React from 'react';
import {
  EDocumentCategory,
  EDocumentVisibilityStatus,
  IDocument,
  TDocumentCategory,
} from 'FeatureModules/Document/types/document.types';
import { EDocumentOverviewType } from 'FeatureModules/Document/types/document.types';
import { removeGraphConnections } from 'utils/graphql.utils';
import CertificationAutoComplete from '../CertificationAutoComplete';
import DocumentCategory from '../../utils/documentCategory.utils';

interface IDocumentForm {
  onDelete: (documentId: string) => void;
  onSubmit: (values: TCreateDocumentInput) => void;
  document: IDocument;
  documentType?: keyof typeof EDocumentOverviewType;
}

const CertificationContainer = styled(Grid)(({ theme }) => ({
  margin: '0 4px',
}));

const CertificationBox = styled(Box)(({ theme }) => ({
  background: '#fff',
  borderRadius: 4,
  paddingTop: 12,
  paddingBottom: 12,
}));

const InUseContainer = styled(Box)(({ theme }) => ({
  background: '#fff',
  borderRadius: 4,
  borderColor: theme.custom.colors.blueFrost,
  borderWidth: 1,
  borderStyle: 'solid',
}));

const FormContainer = styled(Box)(({ theme }) => ({
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.custom.colors.actionButtonHover,
  },
}));

const EditDocumentForm: React.FC<IDocumentForm> = ({
  document,
  onSubmit,
  onDelete,
  documentType,
}) => {
  const { openDialog, closeDialog } = useDialog();
  const documentCategoryOptions = Object.values(EDocumentCategory);

  const certificateCatalogItemId =
    !document.certificateCatalogItem && document.category === EDocumentCategory.CERTIFICATE
      ? OTHER_CERTIFICATE_ID
      : document.certificateCatalogItem?.id;

  const sanitizedInitialDocument = document ? omitBy(document, isEmpty) : {};

  const initialValues = mergeDefaults(documentSchema.default(), {
    ...sanitizedInitialDocument,
    category: document.category,
    certificateCatalogItemId: certificateCatalogItemId || null,
    linkedSites: document.linkedSites ? removeGraphConnections(document.linkedSites) : [],
  });
  const documentIsUsed = !!document.usageCount && document.usageCount > 0;
  const formIsDisabled = documentIsUsed;

  const handleDeleteDocument = () => {
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Delete document',
        displayCloseButton: true,
        text: 'Are you sure you want to delete this document? All data will be lost and you will not be able to recover this document.',
        submitText: 'Delete',
        onSubmit: () => {
          onDelete(document.id);
        },
        onCancel: () => {
          closeDialog();
        },
      },
    });
  };

  return (
    <Formik<TCreateDocumentInput>
      initialValues={initialValues}
      validationSchema={documentSchema}
      enableReinitialize
      onSubmit={values => onSubmit(values)}
    >
      {({ isSubmitting, setFieldValue, values, errors }) => {
        const category = values.category;
        const isCertificate = category === EDocumentCategory.CERTIFICATE;
        const disableDateFields = !category;

        const yearsFromIssuedDate = category
          ? DocumentCategory[category].yearsFromIssuedDate
          : undefined;

        return (
          <Form>
            {documentIsUsed && documentType === 'INTERNAL' && (
              <InUseContainer p={2} mb={3}>
                <ThemeTypography variant="BODY_SMALL">
                  This document is used as proof in {document.usageCount} impact claims, and can
                  therefore not be modified or deleted until the document is replaced for each
                  impact claim, or until these impact claims have been archived or deleted.
                </ThemeTypography>
              </InUseContainer>
            )}
            <FormContainer>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FieldWrapper label="Document title">
                    <Field
                      component={TextInputFileExtension}
                      name="title"
                      variant="outlined"
                      fullWidth
                      disabled={formIsDisabled}
                    />
                  </FieldWrapper>
                </Grid>
                <Grid item xs={12}>
                  <FieldWrapper label="Document type">
                    <Field
                      component={DropDown}
                      errorMsg={errors['category']}
                      name="category"
                      label="Select type"
                      variant="outlined"
                      fullWidth
                      currentValue={values?.category}
                      options={documentCategoryOptions}
                      onChange={(item: TDocumentCategory) => setFieldValue('category', item)}
                      renderItems={(item: TDocumentCategory) => (
                        <div>{DocumentCategory[item].label}</div>
                      )}
                      disabled={formIsDisabled}
                    />
                  </FieldWrapper>
                </Grid>
                {isCertificate && !formIsDisabled && (
                  <CertificationContainer item xs={12}>
                    <CertificationBox px={1.5}>
                      <FieldWrapper label="Select certification">
                        <Field
                          name="certificateCatalogItemId"
                          component={CertificationAutoComplete}
                        />
                      </FieldWrapper>
                    </CertificationBox>
                  </CertificationContainer>
                )}
                <Grid item xs={12}>
                  <FieldWrapper
                    label="Confidentiality"
                    tooltip={{
                      helperText: (
                        <div>
                          <p>
                            The confidentiality level decides who can view your document. Only users
                            from your organisation can edit this setting.
                          </p>
                          <p>
                            <b>Public</b>: Visible to everyone, including consumers when document is
                            used as proof for an impact claim.
                          </p>
                          <p>
                            <b>Private</b>: Users from your organisation can view this document. It
                            can also be reviewed by the system when used as proof for impact claims,
                            but is hidden to all others.
                          </p>
                        </div>
                      ),
                      variant: 'INFO',
                    }}
                  >
                    <Field
                      component={DropDown}
                      name="visibility"
                      variant="outlined"
                      fullWidth
                      disabled={formIsDisabled}
                      currentValue={values?.visibility}
                      options={documentVisibilityOptions}
                      keyField="key"
                      onChange={(item: EDocumentVisibilityStatus) =>
                        setFieldValue('visibility', item)
                      }
                      data-testid="document-visibility-dropdown"
                      renderItems={(item: TVisibilityStatusKeyValue) => (
                        <Box display="flex" gap={1} alignItems="center">
                          <span>{item.icon}</span>
                          <span>{item.value}</span>
                        </Box>
                      )}
                    />
                  </FieldWrapper>
                </Grid>

                <Grid item xs={12}>
                  <PartnerAutocompleteWrapper
                    name="ownedBy"
                    size="medium"
                    labelVariant="medium"
                    disabled={formIsDisabled}
                  />
                </Grid>

                <Grid item xs={12}>
                  <SitesAutocompleteWrapper
                    name="linkedSites"
                    disabled={formIsDisabled}
                    filterByOwnerId={values.ownedBy?.id}
                    label="Link to site(s) or cluster(s)"
                    helperText="Select a site to link this document to, if the document refers to a specific site (e.g. a risk mitigation proof document for a certain farm)."
                    onChange={linkedSites => {
                      // If the linked sites change, we need update the ownedBy field accordingly with the first sites owner
                      if (linkedSites && linkedSites.length > 0) {
                        const firstSiteOwner = linkedSites[0].ownedBy;
                        setFieldValue('ownedBy', firstSiteOwner);
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FieldWrapper label="Issued Date">
                    <DatePickerField
                      disabled={disableDateFields || formIsDisabled}
                      name="issuedDate"
                      placeholder="Select date"
                      data-testid="date-picker-issued-date"
                      placement="TOP"
                    />
                  </FieldWrapper>
                </Grid>
                <Grid item xs={12}>
                  <FieldWrapper
                    label="Expiry Date"
                    info={
                      !!yearsFromIssuedDate
                        ? `${yearsFromIssuedDate} year(s) validity period`
                        : undefined
                    }
                  >
                    <DatePickerField
                      disabled={disableDateFields || !!yearsFromIssuedDate || formIsDisabled}
                      name="expiryDate"
                      placeholder="Select date"
                      data-testid="date-picker-expiry-date"
                      dependentField="issuedDate"
                      yearsAddedFromDependentField={yearsFromIssuedDate}
                      placement="TOP"
                      align="LEFT"
                    />
                  </FieldWrapper>
                </Grid>

                <Grid item xs={12}>
                  <Field
                    name="comment"
                    component={TextInputField}
                    variant="outlined"
                    fullWidth
                    disabled={formIsDisabled}
                    multiline
                    maxlength={300}
                    maxRows={5}
                    minRows={3}
                    placeholder="Enter note"
                    label="Note"
                    helperText="Add notes to your uploaded document to clarify something (e.g. give context to the document if it is used for risk mitigation)."
                  />
                </Grid>
              </Grid>
            </FormContainer>

            <Box mt={4} display="flex">
              <ThemeButton
                size="large"
                color="WHITE"
                onClick={handleDeleteDocument}
                disabled={formIsDisabled}
                startIcon={<Trash size={14} color="black" />}
                data-testid="delete-document-btn"
              >
                Delete document
              </ThemeButton>
              <Box ml={2}>
                <ThemeButton
                  loading={isSubmitting}
                  size="large"
                  color="PRIMARY"
                  type="submit"
                  data-testid="save-document-btn"
                  disabled={formIsDisabled || isEqual(initialValues, values)}
                >
                  Save changes
                </ThemeButton>
              </Box>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default EditDocumentForm;
